import { useQuery } from '@tanstack/react-query'
import { SEO_SERVICE_BASE_URL } from '@pepper/utils/src/api/config'
import axios from '@pepper/utils/src/function/axiosInstance'
import { topicsKey } from './queryKey'

import { SuggestedTopic } from 'types/topic'

type QueryParams = {
  page: number
  limit: number
  topic?: string[]
  keyword?: string[]
  lte?: number
  gte?: number
  exists?: boolean
  sort?: string
}

type Parameters = {
  domainId: string
  companyId: string
  queryParams: QueryParams
  projectId?: string
  themeId?: string
  themeKeywordIds?: string[]
}

export type Response = {
  records: SuggestedTopic[]
  total_records: number
  job_status?: 'processing'
}

const getExistsParams = (queryParams: QueryParams) => {
  if (queryParams.exists) {
    return ['project_id:false', 'self_top_position:false']
  }

  return ['project_id:false']
}

const fetchTopicSuggestions = async ({
  domainId,
  companyId,
  projectId,
  themeId,
  themeKeywordIds,
  queryParams,
}: Parameters): Promise<Response> => {
  const themeKeywordIdParam = themeKeywordIds
    ? `?theme_keyword_id=${themeKeywordIds?.join('&theme_keyword_id=')}`
    : ''
  const url = `${SEO_SERVICE_BASE_URL}/companies/${companyId}/topics/suggestions${themeKeywordIdParam}`

  const { lte, gte } = queryParams
  try {
    const response = await axios.get(url, {
      params: {
        project_id: projectId,
        domain_id: domainId,
        // Required to send this true if triggered from Keyword to Topic Creation from New Topic Dropdown
        project_keywords: !themeId && !themeKeywordIds ? true : undefined,
        theme_id: themeId,
        ...queryParams,
        exists: getExistsParams(queryParams),
        ...(lte !== undefined && {
          lte: `self_top_position:${lte}`,
        }),
        ...(gte !== undefined && {
          gte: `self_top_position:${gte}`,
        }),
      },
      paramsSerializer: {
        indexes: null,
      },
    })

    return response.data
  } catch (error) {
    return error
  }
}

export const useTopicSuggestions = ({
  projectId,
  domainId,
  companyId,
  themeId,
  themeKeywordIds,
  enabled,
  queryParams,
}: Parameters & {
  enabled: boolean
}) => {
  const { isLoading, isFetching, isError, error, refetch, data } = useQuery({
    queryKey: topicsKey.getSuggestedTopicsKey(
      themeId,
      themeKeywordIds ?? [],
      queryParams
    ),
    queryFn: () =>
      fetchTopicSuggestions({
        projectId,
        domainId,
        companyId,
        themeId,
        themeKeywordIds,
        queryParams,
      }),
    enabled,
    refetchInterval: _data => {
      // Polling until we get data,
      // ideally BE should send status key
      const isProcessing =
        !!_data?.job_status ||
        _data?.records?.some(record =>
          ['pending', 'processing', 'error'].includes(
            record.keyword_generation_status
          )
        )
      return isProcessing ? 5000 : false
    },
  })

  return {
    data,
    error,
    refetch,
    isError,
    isLoading,
    isFetching,
  }
}
